/**
 * Get User Profile - Query Hook
 * Following Bulletproof React three-layer pattern for GET operations
 *
 * Layer 1: Pure fetcher function
 * Layer 2: Query options factory
 * Layer 3: React Query hook
 *
 * Fetches complete user profile including base profile (user_profiles),
 * avatar data (file_uploads join), and role-specific profile data
 * (learner_user_profiles, employer_user_profiles, or platform_user_profiles).
 *
 * Cache Invalidation:
 * - Use profileQueryKeys.profile(userId) to invalidate specific user's profile
 * - Use profileQueryKeys.all to invalidate all profile-related data
 *
 * Usage Examples:
 * ```typescript
 * // Basic usage with useCurrentUser
 * const { id, userTypes } = useCurrentUser();
 * const userType = userTypes[0] as UserType;
 * const { data: profile, isLoading } = useUserProfile({ userId: id!, userType });
 *
 * // With custom configuration
 * const { data } = useUserProfile({
 *   userId: id!,
 *   userType,
 *   queryConfig: { staleTime: 1000 * 60 * 10 }, // 10 minutes
 * });
 *
 * // Using query options for prefetching
 * await queryClient.prefetchQuery(getUserProfileQueryOptions(userId, userType));
 * ```
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { QueryConfig } from '@/lib/react-query';
import { profileQueryKeys } from './query-keys';
import {
  UserProfileComplete,
  UserType,
  LearnerProfileRow,
  EmployerProfileRow,
  PlatformProfileRow,
} from './types';

/**
 * Layer 1: Pure fetcher function
 * Fetches complete user profile including base profile, avatar, and role-specific data.
 *
 * Fetch Strategy:
 * 1. Fetch base profile from user_profiles with file_uploads join for avatar
 * 2. Conditionally fetch role-specific profile based on userType
 * 3. Combine into single result object
 *
 * @param userId - User UUID from auth.users
 * @param userType - User type: 'learner' | 'employer' | 'platform'
 * @returns Complete user profile with role-specific data, or null if not found
 * @throws Error if database query fails (role-specific errors are logged but not thrown)
 */
export const getUserProfile = async (
  userId: string,
  userType: UserType
): Promise<UserProfileComplete | null> => {
  // 1. Fetch base profile with avatar join
  const { data: baseProfile, error: baseError } = await supabase
    .from('user_profiles')
    .select(
      `
      *,
      file_uploads!avatar_file_id(*)
    `
    )
    .eq('user_id', userId)
    .single();

  if (baseError) {
    console.error('Base profile fetch error:', baseError);
    throw baseError;
  }

  if (!baseProfile) {
    return null;
  }

  // 2. Conditionally fetch role-specific profile based on user type
  let learnerProfile: LearnerProfileRow | null = null;
  let employerProfile: EmployerProfileRow | null = null;
  let platformProfile: PlatformProfileRow | null = null;

  if (userType === 'learner') {
    const { data, error } = await supabase
      .from('learner_user_profiles')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();

    if (error) {
      console.error('Learner profile fetch error:', error);
      // Don't throw - role profile is optional
    } else {
      learnerProfile = data;
    }
  } else if (userType === 'employer') {
    const { data, error } = await supabase
      .from('employer_user_profiles')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();

    if (error) {
      console.error('Employer profile fetch error:', error);
      // Don't throw - role profile is optional
    } else {
      employerProfile = data;
    }
  } else if (userType === 'platform') {
    const { data, error } = await supabase
      .from('platform_user_profiles')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();

    if (error) {
      console.error('Platform profile fetch error:', error);
      // Don't throw - role profile is optional
    } else {
      platformProfile = data;
    }
  }

  // 3. Return combined result matching UserProfileComplete interface
  return {
    ...baseProfile,
    file_uploads: baseProfile.file_uploads ?? null,
    learner_user_profiles: learnerProfile,
    employer_user_profiles: employerProfile,
    platform_user_profiles: platformProfile,
  };
};

/**
 * Layer 2: Query options factory
 * Provides configuration for React Query with type-safe query keys.
 *
 * @param userId - User UUID from auth.users
 * @param userType - User type: 'learner' | 'employer' | 'platform'
 * @returns Query options object for use with useQuery or prefetchQuery
 */
export const getUserProfileQueryOptions = (userId: string, userType: UserType) => ({
  queryKey: profileQueryKeys.profile(userId),
  queryFn: () => getUserProfile(userId, userType),
  staleTime: 1000 * 60 * 5, // 5 minutes
  enabled: !!userId && !!userType,
});

/**
 * Hook options interface for useUserProfile
 */
export interface UseUserProfileOptions {
  /** User UUID from auth.users */
  userId: string;
  /** User type: 'learner' | 'employer' | 'platform' */
  userType: UserType;
  /** Optional query configuration overrides */
  queryConfig?: QueryConfig<typeof getUserProfileQueryOptions>;
}

/**
 * Layer 3: React Query hook
 * Provides data, loading state, and error handling for user profile.
 *
 * @param options - Hook options including userId, userType, and optional queryConfig
 * @returns React Query result with UserProfileComplete or null
 *
 * @example
 * ```typescript
 * const { data: profile, isLoading, error } = useUserProfile({
 *   userId: currentUserId,
 *   userType: 'learner',
 * });
 *
 * if (isLoading) return <Spinner />;
 * if (error) return <Error message={error.message} />;
 * if (!profile) return <NotFound />;
 *
 * return <ProfileView profile={profile} />;
 * ```
 */
export const useUserProfile = ({
  userId,
  userType,
  queryConfig,
}: UseUserProfileOptions) => {
  return useQuery({
    ...getUserProfileQueryOptions(userId, userType),
    ...queryConfig,
  });
};
